import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, Loader2, ArrowLeft, Eye, EyeOff, RefreshCw, Play, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login, googleLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    setShowResendVerification(false);
    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      if (error.message && error.message.toLowerCase().includes('verify')) {
        setShowResendVerification(true);
        toast.error('Please verify your email before logging in');
      } else {
        toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setResendLoading(true);
    try {
      await axios.post(`${API}/api/auth/resend-verification`, { email }, { withCredentials: true });
      toast.success('Verification email sent! Please check your inbox.');
      setShowResendVerification(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend verification email');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "#F5F5F0" }} data-testid="login-page">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-16 lg:px-24">
        <div className="max-w-md w-full mx-auto">
          {/* Back to home */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm transition-colors mb-8"
            style={{ color: "rgba(26,26,26,0.5)" }}
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png" 
              alt="Prysh AI" 
              className="w-10 h-10 object-contain" 
            />
            <span className="font-display text-xl font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className="text-3xl font-display font-semibold mb-2" style={{ color: "#1A1A1A" }}>Welcome back</h1>
            <p className="mb-8" style={{ color: "rgba(26,26,26,0.5)" }}>
              Sign in to continue creating amazing video ads
            </p>
          </motion.div>

          {/* Google Sign In */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 gap-3 rounded-full bg-white border"
              style={{ borderColor: "rgba(0,0,0,0.12)", color: "#1A1A1A" }}
              onClick={googleLogin}
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>
          </motion.div>

          {/* Divider */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full" style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="px-3" style={{ backgroundColor: "#F5F5F0", color: "rgba(26,26,26,0.4)" }}>Or continue with email</span>
            </div>
          </div>

          {/* Login Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: "#1A1A1A" }}>Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(26,26,26,0.35)" }} />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-12 bg-white rounded-xl"
                  style={{ borderColor: "rgba(0,0,0,0.1)" }}
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" style={{ color: "#1A1A1A" }}>Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm font-medium hover:underline"
                  style={{ color: "#FF5A36" }}
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(26,26,26,0.35)" }} />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 h-12 bg-white rounded-xl"
                  style={{ borderColor: "rgba(0,0,0,0.1)" }}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: "rgba(26,26,26,0.4)" }}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Resend Verification Email */}
            {showResendVerification && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="rounded-xl p-4"
                style={{ backgroundColor: "rgba(255,90,54,0.06)", border: "1px solid rgba(255,90,54,0.15)" }}
              >
                <p className="text-sm mb-3" style={{ color: "rgba(26,26,26,0.7)" }}>
                  Your email is not verified. Please check your inbox or resend the verification email.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleResendVerification}
                  disabled={resendLoading}
                  className="w-full rounded-full"
                  data-testid="resend-verification-btn"
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </Button>
              </motion.div>
            )}

            <Button 
              type="submit" 
              className="w-full h-12 rounded-full text-base font-medium border-0"
              style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </motion.form>

          {/* Sign up link */}
          <p className="text-center text-sm mt-8" style={{ color: "rgba(26,26,26,0.5)" }}>
            Don't have an account?{' '}
            <Link 
              to="/signup" 
              className="font-medium hover:underline"
              style={{ color: "#FF5A36" }}
              data-testid="signup-link"
            >
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Visual */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden" style={{ backgroundColor: "#1A1A1A" }}>
        {/* Subtle glow */}
        <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-[120px]" style={{ backgroundColor: "rgba(255,90,54,0.15)" }} />
        <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] rounded-full blur-[100px]" style={{ backgroundColor: "rgba(255,90,54,0.08)" }} />
        
        <div className="max-w-lg text-center relative z-10">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{ backgroundColor: "rgba(255,90,54,0.15)" }}>
            <Play className="w-10 h-10 ml-1" style={{ color: "#FF5A36" }} />
          </div>
          <h2 className="text-2xl font-display font-semibold mb-4 text-white">
            Create Stunning Video Ads
          </h2>
          <p className="text-white/50 leading-relaxed">
            Join thousands of creators using AI to generate scroll-stopping video ads in minutes. 
            From product URL to polished video, we've got you covered.
          </p>
          <div className="flex items-center justify-center gap-6 mt-10">
            {["AI Video", "Smart Editing", "Auto Ads"].map((tech) => (
              <div key={tech} className="flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" style={{ color: "#FF5A36" }} />
                <span className="text-xs font-medium text-white/40">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
